import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Gate } from './components/Gate';
import { ItemDetail } from './components/ItemDetail';
import { NightDim } from './components/NightDim';
import { OfflineBanner } from './components/OfflineBanner';
import { PhoneLinkBar } from './components/PhoneLinkBar';
import { Rail } from './components/Rail';
import { TextPrompt } from './components/TextPrompt';
import { Toast } from './components/Toast';
import { PATHS } from './navigation';
import { t } from './theme';
import { useStore } from './store';

/**
 * Everything behind the household session: the rail, the current screen and
 * the overlays that float above all of them.
 *
 * The paired-phone view deliberately sits outside this layout — it has its own
 * scoped token and must not bootstrap a household session it cannot have.
 */
export function Shell() {
  const ready = useStore((s) => s.ready);
  const authed = useStore((s) => s.authed);
  const setupComplete = useStore((s) => s.settings.setupComplete);
  const bootstrap = useStore((s) => s.bootstrap);
  const { pathname } = useLocation();

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (!ready) return <div style={{ width: '100vw', height: '100dvh', background: t.bg }} />;
  if (!authed) return <Gate />;

  // Until the household exists there is nowhere else to be, and once it does
  // the wizard is not somewhere to go back to.
  const onSetup = pathname === PATHS.setup;
  if (!setupComplete && !onSetup) return <Navigate to={PATHS.setup} replace />;
  if (setupComplete && onSetup) return <Navigate to={PATHS.dash} replace />;

  return (
    <div
      style={{
        width: '100vw',
        height: '100dvh',
        overflow: 'hidden',
        background: t.bgGradient,
        color: t.ink,
        display: 'flex',
        position: 'relative',
        padding: 14,
        gap: 8,
      }}
    >
      {setupComplete && <Rail />}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </div>
      {setupComplete && <ItemDetail />}
      <TextPrompt />
      <NightDim />
      <PhoneLinkBar />
      <OfflineBanner />
      <Toast />
    </div>
  );
}
